import { useEffect, useMemo, useRef, useState } from 'react';
import { EMOJI_GROUPS } from '../emoji-data';

type Emoji = { char: string; name: string };
type Group = { name: string; emoji: Emoji[] };

/** Enough to scroll through; past this a search term is the faster route. */
const MAX_SEARCH_RESULTS = 240;

/** One short glyph per group for the category strip. */
const GROUP_ICONS: Record<string, string> = {
  'Smileys & Emotion': '😀',
  'People & Body': '👋',
  'Animals & Nature': '🐻',
  'Food & Drink': '🍔',
  'Travel & Places': '🚗',
  Activities: '⚽',
  Objects: '💡',
  Symbols: '❤️',
  Flags: '🏁',
};

/** The packed "<emoji> <name>" rows are only split when the picker first opens. */
const parseGroups = (): Group[] =>
  EMOJI_GROUPS.map((group) => ({
    name: group.name,
    emoji: group.data.split('\n').map((row) => {
      const separator = row.indexOf(' ');
      return { char: row.slice(0, separator), name: row.slice(separator + 1) };
    }),
  }));

type Props = {
  targetName: string;
  onPick: (emoji: string) => void;
  onClose: () => void;
};

export const EmojiPicker = ({ targetName, onPick, onClose }: Props) => {
  const groups = useMemo(parseGroups, []);
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState(groups[0]?.name ?? '');
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

  const shown = useMemo(() => {
    if (terms.length === 0) {
      return groups.find((group) => group.name === activeGroup)?.emoji ?? [];
    }
    const matches: Emoji[] = [];
    for (const group of groups) {
      for (const emoji of group.emoji) {
        if (terms.every((term) => emoji.name.includes(term))) {
          matches.push(emoji);
          if (matches.length >= MAX_SEARCH_RESULTS) return matches;
        }
      }
    }
    return matches;
    // `terms` is derived from query; depending on the joined form keeps the
    // memo from recomputing on every keystroke that doesn't change the words.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, activeGroup, terms.join(' ')]);

  // Jump back to the top whenever the visible set changes, otherwise a search
  // lands you scrolled into the middle of the previous results.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [activeGroup, query]);

  return (
    <div
      className="overlay overlay-picker"
      onMouseDown={(event) => {
        if (!panelRef.current?.contains(event.target as Node)) onClose();
      }}
    >
      <div className="emoji-picker" ref={panelRef} role="dialog" aria-label={`Throw something at ${targetName}`}>
        <header className="emoji-picker-header">
          <h2>Throw at {targetName}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <input
          autoFocus
          className="emoji-search"
          type="search"
          value={query}
          placeholder="Search all emoji…"
          onChange={(event) => setQuery(event.target.value)}
        />

        {terms.length === 0 && (
          <div className="emoji-tabs" role="tablist">
            {groups.map((group) => (
              <button
                key={group.name}
                type="button"
                role="tab"
                aria-selected={group.name === activeGroup}
                className={`emoji-tab${group.name === activeGroup ? ' emoji-tab-active' : ''}`}
                title={group.name}
                onClick={() => setActiveGroup(group.name)}
              >
                {GROUP_ICONS[group.name] ?? '•'}
              </button>
            ))}
          </div>
        )}

        <div className="emoji-grid" ref={listRef}>
          {shown.map((emoji) => (
            <button
              key={emoji.char}
              type="button"
              className="emoji-cell"
              title={emoji.name}
              aria-label={emoji.name}
              onClick={() => onPick(emoji.char)}
            >
              {emoji.char}
            </button>
          ))}
          {shown.length === 0 && <p className="emoji-empty">Nothing matches “{query.trim()}”.</p>}
        </div>
      </div>
    </div>
  );
};
